// src/components/header/logblock/EligeEmpresa.tsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonIconos } from "../../ui";
import TooltipLogBlock from "../../../tooltip/TooltipLogBlock";
import { SvgEditaProfesional } from "../../../icons";
import PopupEmpresas from "../../popup/PopupEmpresas";
import { jwtDecode } from "jwt-decode";

type Empresa = { id: string; nombre: string };

// 🟢 URL base del backend definida en .env
import { safeFetch } from "@/api/safeFetch";


const EligeEmpresa: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();

  // Datos de usuario (ejemplo)
  const [usuario] = useState<{ nombre: string; apellidos: string }>({
    nombre: "José",
    apellidos: "Pérez",
  });

  // 1) Función para abrir el popup y cargar empresas
  const abrirPopup = async () => {
    setIsOpen(true);
    setIsLoading(true);
    setErrorMsg("");
    setEmpresas([]);

    try {
      // 1️⃣ Obtener token del localStorage
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Token no encontrado.");

      // 2️⃣ Decodificar token
      const decoded: any = jwtDecode(token);
      let usuarioId: number | undefined;

      // 3️⃣ Intentar obtener usuarioId directamente del token
      if (decoded.usuario_id) usuarioId = Number(decoded.usuario_id);
      else if (decoded.id) usuarioId = Number(decoded.id);
      else if (decoded.sub) usuarioId = Number(decoded.sub);

      // 4️⃣ Si no tenemos usuarioId válido, usar username
      if (!usuarioId || isNaN(usuarioId)) {
        const username = decoded.username;
        if (!username) throw new Error("Token inválido: no contiene usuarioId ni username.");



        const resUsuario = await safeFetch(`/api/usuarios/idUsername/${username}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!resUsuario.ok) {
          const text = await resUsuario.text();
          throw new Error(`Error buscando usuario: ${text}`);
        }

        const userData = await resUsuario.json();
        if (!userData?.id) throw new Error("Usuario no encontrado en backend.");

        usuarioId = Number(userData.id);
      }


      // 5️⃣ Llamada a API para obtener profesionales/empresas
      const resProfesionales = await safeFetch(`/api/profesionales/usuario/${usuarioId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!resProfesionales.ok) {
        const text = await resProfesionales.text();
        throw new Error(`Error obteniendo profesionales: ${text}`);
      }

      const data = await resProfesionales.json();

      if (!Array.isArray(data.profesionales)) {
        throw new Error("Respuesta inesperada: profesionales no es un array.");
      }

      // 6️⃣ Mapear resultados a empresas
      const profesionalesFormateados: Empresa[] = data.profesionales.map((p: any) => ({
        id: String(p.id),
        nombre: p.nombre,
      }));

   

      setEmpresas(profesionalesFormateados);
    } catch (err: any) {
      console.error("❌ Error abriendo popup de empresas:", err);
      setErrorMsg(err.message || "Error cargando profesionales.");
      setEmpresas([]);
    } finally {
      setIsLoading(false);
    }
  };


  // ✅ Ahora acepta un array de empresas con el mismo nombre
// Cuando el usuario selecciona una empresa/profesional:
const handleEmpresaClick = (empresasSeleccionadas: Empresa[]) => {
  if (empresasSeleccionadas.length === 0) return;

  setIsOpen(false);
  const profesionalId = empresasSeleccionadas[0].id; // tomamos el profesional_id real
  navigate(`/profesionales/${profesionalId}/profesiones`);
};



  return (
    <div>
      <TooltipLogBlock label="Editar profesional">
        <ButtonIconos onClick={abrirPopup}>
          <SvgEditaProfesional />
        </ButtonIconos>
      </TooltipLogBlock>

      <PopupEmpresas
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        empresas={empresas}
        nombre_usuario={usuario.nombre}
        onSelectEmpresa={handleEmpresaClick}
        isLoading={isLoading}
        errorMsg={errorMsg}
      />
    </div>
  );

};

export default EligeEmpresa;
